import { AuthContext, useAuth } from 'context/AuthContext';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { getChallengeHome, getChallengeUser } from 'services/challenges.service';
import Button from 'components/ui/button';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Tippy from '@tippyjs/react';
import { getUserEquipeType } from 'services/dashboard.service';
import { getNamedDates } from 'services/utils';
import { isGestorSemCadastro } from 'utils/gestor';
import { SESSION_KEYS, readOncePerSession } from 'utils/session-reads';
import { engagementReadsNeeded, visibleEngagementCards } from './engagement-cards';
import { DeadlineNote, ProgramItemRow, ProgramSection } from './program-section';

export function Company() {
  const navigate = useNavigate();
  const { user, userInfo, cycleSelected } = useAuth();

  const [homeChallenge, setHomeChallenge] = useState<any>([]);

  const [total, setTotal] = useState<number>();
  const [totalCompleted, setTotalCompleted] = useState<number>();

  const [totalGestor, setTotalGestor] = useState<number>();
  const [totalCompletedGestor, setTotalCompletedGestor] = useState<number>();

  const [totalRede, setTotalRede] = useState<number>(0);
  const [totalCompletedRede, setTotalCompletedRede] = useState<number>(0);

  const [totalColaboracao, setTotalColaboracao] = useState<number>(0);
  const [totalCompletedColaboracao, setTotalCompletedColaboracao] = useState<number>(0);

  const [totalAcao, setTotalAcao] = useState<number>(0);
  const [totalCompletedAcao, setTotalCompletedAcao] = useState<number>(0);

  const [totalNatal, setTotalNatal] = useState<number>(0);
  const [totalCompletedNatal, setTotalCompletedNatal] = useState<number>(0);

  const [totalCompletedCopa, setTotalCompletedCopa] = useState<number>(0);

  const [optionsCycle, setOptionsCycle] = useState<any>();

  const [userType, setUserType] = useState<any>();

  const { realCycle } = useContext(AuthContext);

  // Deps primitivas (DCE-28): trocar a referência de `user`/`userInfo` sem mudar estes valores
  // não refaz as leituras.
  const chave = userInfo?.chave;
  const cycleId = cycleSelected?._id;
  const isRede = !!user?.extra?.rede;
  const isGestor = isGestorSemCadastro(userInfo);
  const selectedCycle = useMemo(() => realCycle?.find((item: any) => item._id === cycleId), [realCycle, cycleId]);
  const cycleStartDate = selectedCycle?.date_start;
  const cycleTitle = selectedCycle?.title;

  useEffect(() => {
    if (!chave || !cycleId || !realCycle) return;

    let active = true;

    // A regra dos cards decide antes quais leituras fazem sentido (DCE-20, DCE-21).
    const reads = engagementReadsNeeded({ cycleStartDate, cycleTitle, isRede, isGestor });

    async function loadData() {
      const [resp, _userType, data] = await Promise.all([
        readOncePerSession(SESSION_KEYS.challengeHome(), () => getChallengeHome()),
        reads.userType ? getUserEquipeType() : Promise.resolve(undefined),
        reads.challengeCounts ? getChallengeUser(cycleId) : Promise.resolve([]),
      ]);

      if (!active) return;

      setHomeChallenge(resp);
      setUserType(_userType);

      const usuarioData = data.filter((item: any) => {
        if (
          item?.extra?.perfil === 'gestor' ||
          item?.extra?.perfil === 'rede' ||
          item?.extra?.perfil === 'colaboracao' ||
          item?.extra?.perfil === 'acao'
        )
          return false;

        return true;
      });

      const gestorData = data.filter((item: any) => {
        if (item?.extra?.perfil !== 'gestor') return false;

        return true;
      });

      const redeData = data.filter((item: any) => {
        if (item?.extra?.perfil === 'rede') return true;

        return false;
      });

      const colaboracaoData = data.filter((item: any) => {
        if (item?.extra?.perfil === 'colaboracao') return true;

        return false;
      });

      const acaoData = data.filter((item: any) => {
        if (item?.extra?.perfil === 'acao' && !item?.extra?.type) return true;

        return false;
      });

      const natalData = data.filter((item: any) => {
        if (item?.extra?.type === 'natal') return true;

        return false;
      });

      const copaData = data.filter((item: any) => item?.extra?.type === 'copa');

      if (selectedCycle) {
        setOptionsCycle({ ...selectedCycle, label: 'Ciclo ' + selectedCycle.title, value: selectedCycle._id });
      }

      setTotalCompleted(usuarioData.filter((item: any) => item.completed).length);
      setTotal(usuarioData.length);

      setTotalCompletedGestor(gestorData.filter((item: any) => item.completed).length);
      setTotalGestor(gestorData.length);

      setTotalCompletedRede(redeData.filter((item: any) => item.completed).length);
      setTotalRede(redeData.length);

      setTotalCompletedColaboracao(colaboracaoData.filter((item: any) => item.completed).length);
      setTotalColaboracao(colaboracaoData.length);

      setTotalCompletedAcao(acaoData.filter((item: any) => item.completed).length);
      setTotalAcao(acaoData.length);

      setTotalCompletedNatal(natalData.filter((item: any) => item.completed).length);
      setTotalNatal(natalData.length);

      setTotalCompletedCopa(copaData.filter((item: any) => item.completed).length);
    }

    loadData();

    return () => {
      active = false;
    };
  }, [chave, cycleId, cycleStartDate, cycleTitle, isRede, isGestor, realCycle, selectedCycle]);

  const cards = useMemo(
    () =>
      visibleEngagementCards({
        cycleStartDate: optionsCycle?.date_start,
        cycleTitle: optionsCycle?.title,
        isRede,
        isGestor,
        hasHybridOnTeam: userType?.has_hybrid_on_team,
        hasHomeChallenges: !!homeChallenge?.length,
      }),
    [optionsCycle, isRede, isGestor, userType, homeChallenge],
  );

  function addBreakLine(str: string) {
    return str?.replace(/\n/g, '<br />');
  }

  const AcessibilityText = (cycleTxt: any) => {
    const [text, setText] = useState('');

    useEffect(() => {
      let txt = '';
      if (!!cycleTxt && !!cycleTxt.title) {
        if (cycleTxt.title.includes('2S')) {
          txt = cycleTxt.title.toLocaleLowerCase().replace('2s', 'Segundo semestre de ');
        }

        if (cycleTxt.title.includes('1s')) {
          txt = cycleTxt.title.toLocaleLowerCase().replace('1s', 'Primeiro semestre de ');
        }

        setText(txt);
      }
    }, [cycleTxt]);

    return (
      <span className="screen-reader-text" tabIndex={0}>
        {text}
      </span>
    );
  };

  // HOM-01.5: sem card nenhum, a caixa inteira sai — moldura vazia anunciando "atividades extras
  // disponíveis durante este ciclo" leria como falha de carregamento.
  if (cards.length === 0) return null;

  const cycleRange =
    optionsCycle?.date_start && optionsCycle?.date_end
      ? `${format(new Date(optionsCycle.date_start), "dd 'de' MMMM", { locale: ptBR })} a ${format(
          new Date(optionsCycle.date_end),
          "dd 'de' MMMM 'de' yyyy",
          { locale: ptBR },
        )}`
      : undefined;

  const rows: EngagementRowProps[] = [];

  if (cards.includes('fim-de-ano')) {
    rows.push({
      id: 'fim-de-ano',
      title: 'Campanha de Fim de Ano',
      done: totalCompletedNatal,
      total: totalAcao,
      progress: `Você realizou ${totalCompletedNatal} de ${totalAcao} desafio.`,
      deadline: '05 a 12 de dezembro de 2025',
      action: { label: 'Acessar desafio', to: '/desafios/acao' },
    });
  }

  if (cards.includes('joga-junto-simplifique')) {
    rows.push({
      id: 'joga-junto-simplifique',
      title: 'Campanha Joga Junto & Simplifique',
      done: totalCompletedAcao,
      total: totalAcao,
      progress: `Você realizou ${totalCompletedAcao} de ${totalAcao} desafio.`,
      deadline:
        optionsCycle?.date_start <= getNamedDates().ciclo_2S2025 ? '01 de julho a 31 de dezembro de 2025' : cycleRange,
      action: { label: 'Acessar desafio', to: '/desafios/acao' },
    });
  }

  if (cards.includes('arraia')) {
    rows.push({
      id: 'arraia',
      title: 'Arraiá do Reconhecimento',
      done: totalCompletedAcao,
      total: totalAcao,
      progress: `Você realizou ${totalCompletedAcao} de ${totalAcao} ações disponíveis.`,
      deadline: optionsCycle?.date_start <= getNamedDates().ciclo_1S2025 ? '13 de junho a 24 de junho de 2025' : cycleRange,
      action: { label: 'Acessar ação', to: '/desafios/acao' },
    });
  }

  // Álbum BB 2026 — 26/jun a 20/jul/2026 — só no ciclo 1S2026
  if (cards.includes('album-bb-2026')) {
    rows.push({
      id: 'album-bb-2026',
      title: 'Álbum BB 2026',
      done: totalCompletedCopa,
      total: 3,
      progress: `Você concluiu ${totalCompletedCopa} de 3 desafios do Álbum BB 2026.`,
      deadline: '26 de junho a 20 de julho de 2026',
      action: {
        label: 'Acessar desafio',
        to: '/desafios/acao',
      },
    });
  }

  if (cards.includes('desafio-semestral')) {
    rows.push({
      id: 'desafio-semestral',
      title: `Desafio semestral ${optionsCycle?.title ?? ''}`,
      done: totalCompleted ?? 0,
      total: total ?? 0,
      progress: `Você realizou ${totalCompleted ?? 0} de ${total ?? 0} desafios disponíveis.`,
      deadline: cycleRange,
      action: { label: 'Acessar desafios', to: '/desafios/jogador' },
    });
  }

  if (cards.includes('desafio-lideranca')) {
    rows.push({
      id: 'desafio-lideranca',
      title: `Desafio Semestral da Liderança ${optionsCycle?.title ?? ''}`,
      done: totalCompletedGestor ?? 0,
      total: totalGestor ?? 0,
      progress: `Você realizou ${totalCompletedGestor ?? 0} de ${totalGestor ?? 0} desafios disponíveis.`,
      deadline: cycleRange,
      action: { label: 'Acessar desafios', to: '/desafios/gestor' },
    });
  }

  if (cards.includes('bonus-colaboracao')) {
    rows.push({
      id: 'bonus-colaboracao',
      title: `Bônus de Colaboração ${optionsCycle?.title ?? ''}`,
      done: totalCompletedColaboracao,
      total: totalColaboracao,
      progress: `Você realizou ${totalCompletedColaboracao} de ${totalColaboracao} desafios disponíveis.`,
      // HOM-01.3: o ciclo começa em 06/08, mas o Bônus de Colaboração do 2S2026 só vale
      // a partir de 10/08. O prazo é texto fixo aqui em vez de uma data de ciclo
      // corrigida porque `date_start` governa fases, apuração e caches.
      deadline: optionsCycle?.title === '2S2026' ? '10 de agosto a 05 de fevereiro de 2027' : cycleRange,
      action: {
        label: 'Acessar colaboração',
        to: '/desafios/colaboracao',
      },
    });
  }

  if (cards.includes('presente-lancamento')) {
    const canRedeem = totalCompletedRede > 0 && totalCompletedRede >= totalRede;

    rows.push({
      id: 'presente-lancamento',
      title: 'Presente de lançamento',
      done: totalCompletedRede,
      total: totalRede,
      progress: `Você realizou ${totalCompletedRede} de ${totalRede} ações disponíveis.`,
      deadline: cycleRange ? '25 de abril a 24 de maio de 2024' : undefined,
      action: { label: 'Acessar ações', to: '/desafios-lancamento' },
      extra: (
        <Tippy
          arrow={true}
          allowHTML={true}
          className={`${canRedeem ? 'hidden' : ''} tippy rounded-xl bg-black p-2 font-thin text-white`}
          content={<p className="text-xs">Você precisa realizar todos os desafios disponíveis para resgatar o presente.</p>}
        >
          <span>
            <Button
              variant="secundario" size="sm"
              onClick={() => {
                if (canRedeem) navigate('/loja');
              }}
              aria-disabled={!canRedeem}
            >
              Resgatar presente
            </Button>
          </span>
        </Tippy>
      ),
    });
  }

  if (cards.includes('desafios-home')) {
    homeChallenge?.forEach((item: any) => {
      rows.push({
        id: `desafios-home-${item._id}`,
        title: 'Desafio da Festa Junina',
        progress: [item.challenge, item.description].filter(Boolean).join(' '),
        deadline: '13 de junho a 20 de junho de 2025',
        action: { label: 'Envie um correio de elogio', external: true, href: item.extra?.url },
      });
    });
  }

  const eyebrow = !user?.extra?.rede ? optionsCycle?.label : undefined;
  // Com um desafio só, a ação e o prazo vão para o rodapé do painel, como no design (HOME-35);
  // com vários, cada linha mantém os seus para não se perder qual botão é de qual desafio.
  const single = rows.length === 1 ? rows[0] : undefined;

  return (
    <ProgramSection
      program="engajamento"
      name="Ações de Engajamento"
      headline={!user?.extra?.rede ? 'Complete os desafios do ciclo e ganhe pontos extras' : undefined}
      description={
        !user?.extra?.rede ? (
          <p>
            Acompanhe aqui atividades extras disponíveis durante este ciclo, que podem gerar pontuação bônus para você.
            <AcessibilityText {...optionsCycle} />
          </p>
        ) : (
          <p>Criamos 4 ações para você experimentar e conhecer o Joga Junto BB.</p>
        )
      }
      actions={single && <EngagementActions action={single.action} extra={single.extra} />}
      note={single?.deadline && <DeadlineNote>{single.deadline}</DeadlineNote>}
    >
      {rows.map(row => (
        <EngagementRow key={row.id} {...row} eyebrow={eyebrow} inFooter={!!single} />
      ))}
    </ProgramSection>
  );
}

interface EngagementAction {
  label: string;
  to?: string;
  /** `external`: link para fora do app, em nova aba (`href`); senão rota interna (`to`). */
  external?: boolean;
  href?: string;
}

interface EngagementRowProps {
  id: string;
  title: string;
  eyebrow?: string;
  done?: number;
  total?: number;
  progress: string;
  deadline?: string;
  action: EngagementAction;
  extra?: ReactNode;
  /** A ação e o prazo estão no rodapé do painel, não na linha. */
  inFooter?: boolean;
}

/** Destaca a contagem ("5 de 7") no texto de progresso, como no design. */
function highlightCount(progress: string, done?: number, total?: number): ReactNode {
  if (typeof done !== 'number' || typeof total !== 'number') return progress;

  const count = `${done} de ${total}`;
  const index = progress.indexOf(count);
  if (index < 0) return progress;

  return (
    <>
      {progress.slice(0, index)}
      {/* Azul-escuro: o azul sobre o cinza da linha dava 4,34:1 em 11px (CAN-40). */}
      <b className="font-semibold text-brand-blue-dark">{count}</b>
      {progress.slice(index + count.length)}
    </>
  );
}

function EngagementActions({ action, extra }: { action: EngagementAction; extra?: ReactNode }) {
  return (
    <span className="flex flex-wrap items-center gap-3">
      {action.external ? (
        action.href ? (
          <Button variant="solido-destaque" size="sm" href={action.href} target="_blank" rel="noreferrer">
            {action.label}
          </Button>
        ) : (
          <Button variant="solido-destaque" size="sm" disabled>
            {action.label}
          </Button>
        )
      ) : (
        // O nome acessível é o texto visível do botão com "deste ciclo" no fim (WCAG 2.5.3), montado aqui para os dois
        // não divergirem.
        <Button variant="solido-destaque" size="sm" to={action.to || '/'} aria-label={`${action.label} deste ciclo`}>
          {action.label}
        </Button>
      )}
      {extra}
    </span>
  );
}

/** Um desafio do ciclo: rótulo, título e progresso; ação e prazo na linha quando há vários. */
function EngagementRow({ title, eyebrow, done, total, progress, deadline, action, extra, inFooter }: EngagementRowProps) {
  return (
    <ProgramItemRow
      eyebrow={eyebrow ?? 'Ação de engajamento'}
      title={title}
      meta={
        <>
          {highlightCount(progress, done, total)}
          {!inFooter && deadline && (
            <>
              <br />
              Prazo para cumprimento: {deadline}
            </>
          )}
        </>
      }
      action={inFooter ? undefined : <EngagementActions action={action} extra={extra} />}
    />
  );
}
